import { useMemo, useState } from "react"
import { ListChecks, Pencil, Search, X } from "lucide-react"

import { FormCard } from "@/components/FormCard"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Field, FieldLabel } from "@/components/ui/field"
import { Input } from "@/components/ui/input"
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from "@/components/ui/input-group"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import type { OtkDefectSeverity } from "shared"
import {
  severityBadgeVariant,
  severityLabels,
  type NonConformitiesTabState,
  type NonConformityCode,
  type UnlistedIssue,
} from "@/lib/documentEditor"

const MAX_ISSUE_RESULTS = 8

/**
 * document-editor.md's DE8: the annex of non-conformity codes (840 real
 * codes in 20 groups) is registry data this pass does not have and was told
 * not to fabricate — real data belongs behind `GET /api/otk/kod-nevidpovidnosti`
 * (see document-editor.md's API/Data #3), which isn't wired up yet, so the
 * search UI below has nothing to search against for now
 * (`saveDocumentNonconformities` on the backend still resolves the
 * authoritative code/description/severity from its own registry collection
 * at save time regardless of what's shown here).
 */
const nonConformityCodes: NonConformityCode[] = []

function CodeChip({ children }: { children: string }) {
  return (
    <span className="font-mono text-xs font-semibold whitespace-nowrap">
      {children}
    </span>
  )
}

const severityOptions: OtkDefectSeverity[] = ["MINOR", "MAJOR", "DANGEROUS"]

export function NonConformitiesTab({
  value,
  onChange,
}: {
  value: NonConformitiesTabState
  onChange: (patch: Partial<NonConformitiesTabState>) => void
}) {
  const [query, setQuery] = useState("")
  const [dialogOpen, setDialogOpen] = useState(false)
  const [draftSelection, setDraftSelection] = useState<NonConformityCode[]>([])
  const [unlistedType, setUnlistedType] =
    useState<OtkDefectSeverity>("MINOR")
  const [unlistedDescription, setUnlistedDescription] = useState("")

  const results = useMemo(() => {
    const trimmed = query.trim().toLocaleLowerCase()
    if (!trimmed) return []
    const selectedIds = new Set(value.selectedCodes.map((c) => c.id))
    return nonConformityCodes
      .filter(
        (code) =>
          !selectedIds.has(code.id) &&
          (code.code.toLocaleLowerCase().includes(trimmed) ||
            code.description.toLocaleLowerCase().includes(trimmed))
      )
      .slice(0, MAX_ISSUE_RESULTS)
  }, [query, value.selectedCodes])

  function setFound(found: boolean) {
    if (!found) {
      onChange({
        found: false,
        selectedCodes: [],
        hasUnlisted: false,
        unlistedIssues: [],
      })
      setQuery("")
    } else {
      onChange({ found: true })
    }
  }

  function addCode(code: NonConformityCode) {
    onChange({ selectedCodes: [...value.selectedCodes, code] })
    setQuery("")
  }

  function removeCode(id: string) {
    onChange({
      selectedCodes: value.selectedCodes.filter((code) => code.id !== id),
    })
  }

  function addUnlistedIssue() {
    if (!unlistedDescription.trim()) return
    const issue: UnlistedIssue = {
      id: crypto.randomUUID(),
      type: unlistedType,
      description: unlistedDescription.trim(),
    }
    onChange({ unlistedIssues: [...value.unlistedIssues, issue] })
    setUnlistedDescription("")
  }

  function removeUnlistedIssue(id: string) {
    onChange({
      unlistedIssues: value.unlistedIssues.filter((issue) => issue.id !== id),
    })
  }

  return (
    <FormCard>
      <Field required>
        <FieldLabel>Знайдено невідповідності</FieldLabel>
        <RadioGroup
          className="flex gap-4"
          onValueChange={(next) => setFound(next === "yes")}
          value={value.found === null ? "" : value.found ? "yes" : "no"}
        >
          <label className="flex items-center gap-2 text-sm" htmlFor="nc-found-no">
            <RadioGroupItem id="nc-found-no" value="no" />
            Ні
          </label>
          <label className="flex items-center gap-2 text-sm" htmlFor="nc-found-yes">
            <RadioGroupItem id="nc-found-yes" value="yes" />
            Так
          </label>
        </RadioGroup>
      </Field>

      {value.found === true && (
        <div className="mt-6">
          <div className="flex items-end gap-3">
            <Field className="flex-1">
              <FieldLabel htmlFor="nc-search">Код невідповідності</FieldLabel>
              <InputGroup>
                <InputGroupAddon>
                  <Search />
                </InputGroupAddon>
                <InputGroupInput
                  id="nc-search"
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Пошук за кодом або описом"
                  value={query}
                />
              </InputGroup>
            </Field>
            <Button
              onClick={() => {
                setDraftSelection(value.selectedCodes)
                setDialogOpen(true)
              }}
              variant="outline"
            >
              <ListChecks data-icon="inline-start" />
              Переглянути всі коди
            </Button>
          </div>

          {query.trim() !== "" && (
            <div className="mt-2 divide-y rounded-lg border">
              {results.length === 0 ? (
                <p className="p-3 text-sm text-muted-foreground">
                  Код не знайдено
                </p>
              ) : (
                results.map((code) => (
                  <button
                    className="flex w-full items-center justify-between p-3 text-left hover:bg-muted"
                    key={code.id}
                    onClick={() => addCode(code)}
                    type="button"
                  >
                    <span className="flex items-center gap-2">
                      <CodeChip>{code.code}</CodeChip>
                      <span className="truncate">{code.description}</span>
                    </span>
                    <Badge variant={severityBadgeVariant(code.severity)}>
                      {severityLabels[code.severity]}
                    </Badge>
                  </button>
                ))
              )}
            </div>
          )}

          {value.selectedCodes.length > 0 && (
            <div className="mt-4 space-y-2">
              <p className="flex items-center gap-1.5 text-sm font-medium">
                <ListChecks className="size-4" />
                Коди з довідника
              </p>
              {value.selectedCodes.map((code) => (
                <div
                  className="flex items-center justify-between rounded-lg border p-3"
                  key={code.id}
                >
                  <span className="flex items-center gap-2">
                    <CodeChip>{code.code}</CodeChip>
                    <span className="truncate">{code.description}</span>
                  </span>
                  <span className="flex items-center gap-2">
                    <Badge variant={severityBadgeVariant(code.severity)}>
                      {severityLabels[code.severity]}
                    </Badge>
                    <Button
                      aria-label={`Видалити код ${code.code}`}
                      onClick={() => removeCode(code.id)}
                      size="icon-xs"
                      variant="ghost"
                    >
                      <X />
                    </Button>
                  </span>
                </div>
              ))}
            </div>
          )}

          <label className="mt-4 flex items-center gap-2 text-sm" htmlFor="nc-unlisted">
            <Checkbox
              checked={value.hasUnlisted}
              id="nc-unlisted"
              onCheckedChange={(checked) =>
                onChange({ hasUnlisted: checked === true })
              }
            />
            Непередбачені кодами несправності
          </label>

          {value.hasUnlisted && (
            <div className="mt-3 space-y-3 rounded-lg border p-3">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
                <Field className="sm:w-48">
                  <FieldLabel htmlFor="nc-unlisted-type">
                    Тип несправності
                  </FieldLabel>
                  <Select
                    onValueChange={(next) =>
                      setUnlistedType(next as OtkDefectSeverity)
                    }
                    value={unlistedType}
                  >
                    <SelectTrigger id="nc-unlisted-type" length="full-size">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {severityOptions.map((severity) => (
                        <SelectItem key={severity} value={severity}>
                          {severityLabels[severity]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <Field className="flex-1">
                  <FieldLabel htmlFor="nc-unlisted-description">
                    Опис несправності
                  </FieldLabel>
                  <Input
                    id="nc-unlisted-description"
                    onChange={(e) => setUnlistedDescription(e.target.value)}
                    placeholder="Опишіть виявлений дефект"
                    value={unlistedDescription}
                  />
                </Field>
                <Button
                  disabled={!unlistedDescription.trim()}
                  onClick={addUnlistedIssue}
                  type="button"
                >
                  <Pencil data-icon="inline-start" />
                  Додати
                </Button>
              </div>

              {value.unlistedIssues.length > 0 && (
                <div className="space-y-2">
                  <p className="flex items-center gap-1.5 text-sm font-medium">
                    <Pencil className="size-4" />
                    Несправності без коду
                  </p>
                  <div className="divide-y rounded-lg border border-dashed">
                    {value.unlistedIssues.map((issue) => (
                      <div
                        className="flex items-center justify-between p-3 text-sm"
                        key={issue.id}
                      >
                        <span className="flex items-center gap-2">
                          <CodeChip>Власний</CodeChip>
                          <span className="truncate">{issue.description}</span>
                        </span>
                        <span className="flex items-center gap-2">
                          <Badge variant={severityBadgeVariant(issue.type)}>
                            {severityLabels[issue.type]}
                          </Badge>
                          <Button
                            aria-label={`Видалити непередбачену несправність: ${issue.description}`}
                            onClick={() => removeUnlistedIssue(issue.id)}
                            size="icon-xs"
                            variant="ghost"
                          >
                            <X />
                          </Button>
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <p className="mt-4 text-sm text-muted-foreground">
                Значна або небезпечна невідповідність змінює результат на
                негативний, а тип документа — на акт. Обидва значення
                виводяться з цього переліку й не редагуються вручну — щоб їх
                змінити, треба виправити самі коди.
              </p>
            </div>
          )}
        </div>
      )}

      <Dialog onOpenChange={setDialogOpen} open={dialogOpen}>
        <DialogContent className="flex max-h-[80vh] flex-col sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>Коди невідповідності</DialogTitle>
          </DialogHeader>
          <IssueCodesDialogBody
            draftSelection={draftSelection}
            setDraftSelection={setDraftSelection}
          />
          <DialogFooter className="items-center justify-between sm:justify-between">
            <p className="text-sm text-muted-foreground">
              Обрано: {draftSelection.length}
            </p>
            <div className="flex gap-2">
              <Button onClick={() => setDialogOpen(false)} variant="outline">
                Відмінити
              </Button>
              <Button
                onClick={() => {
                  onChange({ selectedCodes: draftSelection })
                  setDialogOpen(false)
                }}
              >
                Застосувати
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </FormCard>
  )
}

function IssueCodesDialogBody({
  draftSelection,
  setDraftSelection,
}: {
  draftSelection: NonConformityCode[]
  setDraftSelection: (next: NonConformityCode[]) => void
}) {
  const [search, setSearch] = useState("")
  const matches = useMemo(() => {
    const trimmed = search.trim().toLocaleLowerCase()
    if (!trimmed) return nonConformityCodes
    return nonConformityCodes.filter(
      (code) =>
        code.code.toLocaleLowerCase().includes(trimmed) ||
        code.description.toLocaleLowerCase().includes(trimmed)
    )
  }, [search])

  function toggle(code: NonConformityCode) {
    const exists = draftSelection.some((c) => c.id === code.id)
    setDraftSelection(
      exists
        ? draftSelection.filter((c) => c.id !== code.id)
        : [...draftSelection, code]
    )
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col border">
      <div className="border-b p-2">
        <InputGroup>
          <InputGroupAddon>
            <Search />
          </InputGroupAddon>
          <InputGroupInput
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Пошук за кодом або описом"
            value={search}
          />
        </InputGroup>
      </div>
      <div className="flex-1 overflow-y-auto p-2">
        {matches.length === 0 ? (
          <p className="p-3 text-center text-sm text-muted-foreground">
            Код не знайдено
          </p>
        ) : (
          matches.map((code) => (
            <button
              className="flex w-full items-center justify-between rounded-md p-2 text-left text-sm hover:bg-muted"
              key={code.id}
              onClick={() => toggle(code)}
              type="button"
            >
              <span className="flex flex-1 items-center gap-2 truncate">
                <CodeChip>{code.code}</CodeChip>
                <span className="truncate">{code.description}</span>
              </span>
              <Badge variant={severityBadgeVariant(code.severity)}>
                {severityLabels[code.severity]}
              </Badge>
            </button>
          ))
        )}
      </div>
    </div>
  )
}
